import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Target, Loader2, Save } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from "recharts";
import { toast } from "sonner";

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

export default function MetasCadastro() {
  const hoje = new Date();
  const [vendedores, setVendedores] = useState([]);
  const [vendedorId, setVendedorId] = useState("");
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [ano, setAno] = useState(hoje.getFullYear());

  const [form, setForm] = useState({
    meta_contatos: 0,
    meta_visitas: 0,
    meta_propostas: 0,
    meta_analise: 0,
    meta_contratos: 0,
    meta_fechamentos: 0,
    valor_meta: 0,
  });
  const [desempenho, setDesempenho] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/comercial/usuarios-metas").then((r) => {
      setVendedores(r.data);
      if (r.data.length && !vendedorId) setVendedorId(r.data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  const carregarDesempenho = async () => {
    if (!vendedorId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/metas/desempenho", { params: { vendedor_id: vendedorId, mes, ano } });
      setDesempenho(data);
      setForm({
        meta_contatos: data.meta?.meta_contatos || 0,
        meta_visitas: data.meta?.meta_visitas || 0,
        meta_propostas: data.meta?.meta_propostas || 0,
        meta_analise: data.meta?.meta_analise || 0,
        meta_contratos: data.meta?.meta_contratos || 0,
        meta_fechamentos: data.meta?.meta_fechamentos || 0,
        valor_meta: data.meta?.valor_meta || 0,
      });
    } catch (e) {
      toast.error("Falha ao carregar desempenho");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDesempenho();
    // eslint-disable-next-line
  }, [vendedorId, mes, ano]);

  const salvar = async () => {
    if (!vendedorId) {
      toast.error("Selecione um vendedor");
      return;
    }
    setSaving(true);
    try {
      await api.post("/metas", {
        vendedor_id: vendedorId,
        mes: Number(mes),
        ano: Number(ano),
        meta_contatos: Number(form.meta_contatos),
        meta_visitas: Number(form.meta_visitas),
        meta_propostas: Number(form.meta_propostas),
        meta_fechamentos: Number(form.meta_fechamentos),
        valor_meta: Number(form.valor_meta),
        qtd_meta: Number(form.meta_fechamentos),
      });
      toast.success("Meta salva!");
      await carregarDesempenho();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar meta");
    } finally {
      setSaving(false);
    }
  };

  const dadosGrafico = useMemo(() => {
    if (!desempenho) return [];
    return [
      { etapa: "Contatos", Meta: desempenho.meta.meta_contatos, Realizado: desempenho.realizado.contatos, pct: desempenho.percentual.contatos },
      { etapa: "Visitas", Meta: desempenho.meta.meta_visitas, Realizado: desempenho.realizado.visitas, pct: desempenho.percentual.visitas },
      { etapa: "Lev. Proposta", Meta: desempenho.meta.meta_propostas, Realizado: desempenho.realizado.propostas, pct: desempenho.percentual.propostas },
      { etapa: "Análise", Meta: desempenho.meta.meta_analise ?? 0, Realizado: desempenho.realizado.analise ?? 0, pct: desempenho.percentual.analise ?? 0 },
      { etapa: "Contrato", Meta: desempenho.meta.meta_contratos ?? 0, Realizado: desempenho.realizado.contratos ?? 0, pct: desempenho.percentual.contratos ?? 0 },
      { etapa: "Fechamento", Meta: desempenho.meta.meta_fechamentos, Realizado: desempenho.realizado.fechamentos, pct: desempenho.percentual.fechamentos },
    ];
  }, [desempenho]);

  const corPct = (p) => (p >= 100 ? "#16a34a" : p >= 70 ? "#F97316" : "#dc2626");

  const anosOpcoes = [hoje.getFullYear() - 1, hoje.getFullYear(), hoje.getFullYear() + 1];

  return (
    <div className="space-y-5" data-testid="metas-cadastro-page">
      <div className="flex items-center gap-3">
        <Target size={28} style={{ color: "#1B3A6B" }} />
        <div>
          <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>Cadastro de Metas</h1>
          <p className="text-sm text-muted-foreground">Defina metas por etapa do funil para cada vendedor</p>
        </div>
      </div>

      <Card className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div>
            <Label>Usuário com meta (Vendedor / Supervisor / Gerente)</Label>
            <Select value={vendedorId} onValueChange={setVendedorId}>
              <SelectTrigger data-testid="meta-vendedor"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                {vendedores.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.nome} {v.role && v.role !== "vendedor" ? `(${v.role})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Mês</Label>
            <Select value={String(mes)} onValueChange={(v) => setMes(Number(v))}>
              <SelectTrigger data-testid="meta-mes"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MESES.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Ano</Label>
            <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {anosOpcoes.map((a) => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <FieldNumber label="Contatos" value={form.meta_contatos} onChange={(v) => setForm({ ...form, meta_contatos: v })} testid="meta-contatos" />
          <FieldNumber label="Visitas" value={form.meta_visitas} onChange={(v) => setForm({ ...form, meta_visitas: v })} testid="meta-visitas" />
          <FieldNumber label="Lev. Proposta" value={form.meta_propostas} onChange={(v) => setForm({ ...form, meta_propostas: v })} testid="meta-propostas" />
          <FieldNumber label="Análise" value={form.meta_analise} onChange={(v) => setForm({ ...form, meta_analise: v })} testid="meta-analise" />
          <FieldNumber label="Contrato" value={form.meta_contratos} onChange={(v) => setForm({ ...form, meta_contratos: v })} testid="meta-contratos" />
          <FieldNumber label="Fechamento" value={form.meta_fechamentos} onChange={(v) => setForm({ ...form, meta_fechamentos: v })} testid="meta-fechamentos" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <FieldNumber label="Receita meta (R$)" value={form.valor_meta} onChange={(v) => setForm({ ...form, valor_meta: v })} testid="meta-receita" />
        </div>

        <div className="mt-4">
          <Button
            data-testid="meta-salvar"
            onClick={salvar}
            disabled={saving || !vendedorId}
            className="text-white"
            style={{ background: "#1B3A6B" }}
          >
            {saving ? <><Loader2 size={14} className="mr-2 animate-spin" /> Salvando…</> : <><Save size={14} className="mr-2" /> Salvar meta</>}
          </Button>
        </div>
      </Card>

      <Card className="p-5">
        <h2 className="text-lg font-bold mb-2" style={{ color: "#1B3A6B" }}>Desempenho — Meta × Realizado</h2>
        {loading ? (
          <div className="text-sm text-muted-foreground py-8 text-center">Carregando…</div>
        ) : desempenho ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
              {dadosGrafico.map((d) => (
                <div key={d.etapa} className="p-3 rounded border bg-white" data-testid={`pct-${d.etapa.toLowerCase()}`}>
                  <div className="text-xs uppercase text-muted-foreground">{d.etapa}</div>
                  <div className="text-2xl font-bold" style={{ color: corPct(d.pct) }}>{d.Realizado}/{d.Meta}</div>
                  <div className="text-xs font-medium" style={{ color: corPct(d.pct) }}>{d.pct}% da meta</div>
                </div>
              ))}
            </div>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={dadosGrafico} layout="vertical" margin={{ left: 30, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="etapa" type="category" width={100} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Meta" fill="#94a3b8" />
                  <Bar dataKey="Realizado">
                    {dadosGrafico.map((d, idx) => (
                      <Cell key={idx} fill={corPct(d.pct)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <div className="text-sm text-muted-foreground py-8 text-center">Selecione um vendedor.</div>
        )}
      </Card>
    </div>
  );
}

const FieldNumber = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input type="number" min={0} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
  </div>
);
